import SubpageLayout from "@/components/SubpageLayout";
import { P, Section } from "@/components/Prose";

const threeEs = [
  { title: "Event", question: "Hvad skete der?", bg: "rgba(91, 127, 126, 0.22)", border: "rgba(91, 127, 126, 0.45)" },
  { title: "Experience", question: "Hvordan blev det oplevet og erfaret?", bg: "rgba(214, 194, 166, 0.5)", border: "rgba(214, 194, 166, 0.9)" },
  { title: "Effect", question: "Og hvilken effekt har det haft?", bg: "rgba(180, 106, 69, 0.22)", border: "rgba(180, 106, 69, 0.5)" },
];

const ThreeEs = () => (
  <div
    data-testid="viden-tre-e-grafik"
    className="flex flex-col items-center gap-8 py-6 sm:flex-row sm:items-start sm:justify-center sm:gap-10"
  >
    {threeEs.map((e) => (
      <div key={e.title} className="flex w-40 flex-col items-center text-center">
        <div
          className="flex h-36 w-36 items-center justify-center rounded-full"
          style={{ backgroundColor: e.bg, border: `1px solid ${e.border}` }}
        >
          <span className="font-serif text-2xl text-[#2F3338]">{e.title}</span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[#2F3338]/75">{e.question}</p>
      </div>
    ))}
  </div>
);

const Viden = () => (
  <SubpageLayout
    overline="TRAUMEBEVIDST UDVIKLING"
    title="Viden"
    art="/brand/phase-grobund.png"
    testId="page-viden"
  >
    <Section id="hvad-er-traumer" title="Hvad er traumer?" subline="Det afhænger også af, hvordan vi forstår dem">
      <P>
        Der findes ikke én entydig definition af, hvad et traume er. Traumer kan forstås på
        forskellige måder og på forskellige niveauer.
      </P>
      <P>
        Vi kan tale om traumer hos det enkelte menneske, men også om traumer, der rammer familier,
        grupper, organisationer og hele samfund.
      </P>
      <figure className="py-4">
        <img
          src="/brand/hvad-er-traumer.png"
          alt="Fem cirkler i hinanden: Individuelle traumer, Familietraumer, Gruppetraumer, Strukturelle traumer og Kollektive traumer"
          data-testid="viden-traume-cirkler"
          className="mx-auto w-full max-w-xl"
        />
      </figure>
      <P>
        Traumer opstår og får betydning i sociale, kulturelle og historiske sammenhænge, og
        påvirkningen kan række ud over det enkelte menneske og på tværs af generationer.
      </P>
      <P>
        Når vi taler om individuelle traumer, læner vi os i Traumebevidst Udvikling blandt andet
        op ad SAMHSA's definition fra 2014:
      </P>
      <div data-testid="viden-samhsa-citat">
        <p className="border-l-2 border-[#B46A45]/60 py-1 pl-6 font-serif text-lg italic leading-relaxed text-[#3F5A47]">
          “Et individuelt traume opstår efter en hændelse, en række hændelser eller et sæt af
          omstændigheder, som den enkelte oplever som fysisk eller emotionelt skadelige eller
          livstruende, og som har vedvarende negative konsekvenser for personens funktionsevne og
          mentale, fysiske, sociale, emotionelle eller spirituelle trivsel.”
        </p>
      </div>
      <P>
        Derfor kan vi heller ikke udefra definere, hvad der har været traumatisk for et andet
        menneske. Vi kan i stedet være nysgerrige på de tre E'er:
      </P>
      <ThreeEs />
      <P>
        Oplevelsen kan blandt andet være præget af magtesløshed eller handlekraft, kontrol eller
        mangel på samme og adgang til støtte og beskyttelse. Kulturelle, historiske og
        kønsmæssige forhold kan samtidig have betydning for menneskets position og
        handlemuligheder under og umiddelbart efter det, der sker.
      </P>
      <p
        data-testid="viden-overlevelse-spoergsmaal"
        className="py-1 font-serif text-xl leading-relaxed text-[#3F5A47] sm:text-2xl"
      >
        Og vi kan spørge: Hvordan overlevede du?
      </p>
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
        <P>
          For når vi interesserer os for, hvordan et menneske har klaret sig igennem det, der
          skete, kan vi også få øje på ressourcer, modhandlinger, handlekraft og de måder,
          mennesket har forsøgt at beskytte sig selv, bevare forbindelse eller skabe sikkerhed.
        </P>
        <img
          src="/brand/hero-dandelion.png"
          alt="Mælkebøtte der spreder sine frø – et billede på udvikling"
          data-testid="viden-overlevelse-maelkeboette"
          className="h-24 w-auto shrink-0"
        />
      </div>
    </Section>

    <Section id="hvad-er-traumereaktioner" title="Hvad er traumereaktioner?" subline="Eller hvad er de ikke?">
      <figure className="py-4">
        <img
          src="/brand/traumereaktioner-trae.png"
          alt="Håndtegnet træ med ord som blade: Mistro, Ustabile relationer, Koncentrationsbesvær, Rusmiddelforbrug, Tvangshandlinger, Uro, Isolation, Ensomhed, Perfektionisme, Risikovillighed, Mareridt, Selvskade, Tristhed, Dissociation, Søvnbesvær, Anhedoni, Ringe selvværd, Skyld, Tomhed, Undgåelse, Skam, Smerter, Hukommelsesbesvær, Vrede, Stemmehøring, Selvmordstanker, Flashbacks, Vagtsomhed og Påtrængende erindringer"
          data-testid="viden-traumereaktioner-trae"
          className="mx-auto w-full max-w-xl"
        />
      </figure>
      <P>
        Traumereaktioner kan både afspejle det, mennesket har erfaret, og de måder, mennesket
        forsøger at mestre følgerne af det på.
      </P>
      <P>
        De kan komme til udtryk på mange forskellige måder. Når vi forstår reaktioner i sammenhæng
        med oplevelser, kan det, der før virkede uforståeligt eller usammenhængende, give mening.
      </P>
      <P>
        På tværs af de mange forskellige udtryk kan vi ofte finde forsøg på at skabe sikkerhed,
        handlekraft eller forbindelse, når noget grundlæggende er blevet rystet: oplevelsen af at
        være sikker, af at kunne handle og have indflydelse eller af at kunne være forbundet med
        og have tillid til andre.
      </P>
      <P>
        Vagtsomhed, kontrol, undgåelse, tilbagetrækning og overtilpasning er blot nogle eksempler
        på udtryk, der også kan have en beskyttende eller mestrende funktion. De kan fortælle os
        noget om, hvad mennesket forsøger at beskytte, genvinde eller mestre.
      </P>
      <P>
        Det betyder ikke, at alle reaktioner er traumereaktioner eller mestringsforsøg, eller at
        vi på forhånd ved, hvad en bestemt reaktion betyder. Men når vi bliver nysgerrige på
        sammenhængen og på reaktionens mulige funktion, kan vi også få øje på ressourcer og
        handlekraft og møde mennesket med større forståelse og omsorg frem for alene at forsøge at
        dæmpe eller ændre reaktionen.
      </P>
      <P>
        Traumer er derfor én af flere mulige forståelser, vi bør have til rådighed, når vi forsøger
        at forstå menneskers reaktioner.
      </P>
    </Section>

    <Section id="hvad-er-traumebevidst-tilgang" title="Hvad er traumebevidst tilgang?">
      <P>
        Traumebevidst tilgang er ikke en bestemt metode eller behandling. Det er en måde at forstå,
        møde og skabe rammer for mennesker på, hvor viden om traumer og traumereaktioner får
        betydning for det, vi faktisk gør.
      </P>
      <P>
        For os tager traumebevidst tilgang afsæt i sikkerhed, handlekraft og forbindelse. Hvis
        erfaringer kan ryste oplevelsen af at være sikker, kunne handle og have indflydelse eller
        være forbundet med andre, må vi skabe møder og rammer, der understøtter netop disse.
        Samtidig må vi aktivt forsøge at mindske risikoen for, at mennesker retraumatiseres eller
        påføres skade i de systemer, der er sat i verden for at hjælpe dem.
      </P>
      <P>
        Det kræver også opmærksomhed på magt og på, hvilken viden der får plads. Mennesker skal
        ikke alene mødes som nogen, vi har viden om, men som mennesker med viden om eget liv og
        egne erfaringer. Traumebevidst tilgang kalder derfor også på en høj grad af ligeværd mellem
        forskellige former for viden og på reel mulighed for at blive hørt og få indflydelse.
      </P>
      <div className="pt-6">
        <P>
          Vi læner os blandt andet op ad SAMHSA's definition af traumebevidst tilgang. Her
          beskrives et traumebevidst program, en organisation eller et system som et, der:
        </P>
        <div
          data-testid="viden-samhsa-punkter"
          className="border-l-2 border-[#B46A45]/60 py-1 pl-6"
        >
          <ul className="space-y-3">
            {[
              "anerkender traumers udbredelse og følger og forstår mulige veje til recovery",
              "genkender tegn på traumer hos individer, familier, personale og andre, der indgår i systemet",
              "responderer ved at integrere viden om traumer i politikker, procedurer og praksis",
              "aktivt søger at forebygge retraumatisering.",
            ].map((punkt) => (
              <li
                key={punkt}
                className="flex gap-3 font-serif text-lg italic leading-relaxed text-[#3F5A47]"
              >
                <span className="mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#B46A45]" />
                {punkt}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm italic text-[#2F3338]/60">
          (Substance Abuse and Mental Health Services Administration, 2014)
        </p>
      </div>
      <P>
        Traumebevidst tilgang kræver, at vi alle tør se indad og begynde hos os selv. Som
        fagpersoner må vi være nysgerrige på vores egne forståelser, sprog og handlinger. Som
        ledere på de rammer og betingelser, vi skaber. Som organisationer på vores kulturer,
        praksisser og organisering. Og som systemer på de strukturer og logikker, der former
        menneskers muligheder.
      </P>
      <P>
        Traumebevidst udvikling begynder dermed også oftest dér, hvor vi selv står, og med det, vi
        kan påvirke.
      </P>
    </Section>
  </SubpageLayout>
);

export default Viden;
